import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { api, call } from "@/core/api/client";
import { ENDPOINTS } from "@/core/api/endpoints";
import { QUERY_KEYS } from "@/core/api/query-client";
import type { components } from "@/core/api/schema";

export type AnalyticsSummary = components["schemas"]["AnalyticsSummary"];
export type AnalyticsDay = components["schemas"]["AnalyticsDay"];
export type AnalyticsMonth = components["schemas"]["AnalyticsMonth"];
export type AnalyticsPlan = components["schemas"]["AnalyticsPlan"];
export type SignupRange = AnalyticsSummary["signups"]["range"];

/** Key numbers, retention and the new-subscriptions chart over `range` (Owner and Manager). */
export function useAnalyticsSummary(brandId: string, range: SignupRange, enabled = true, month?: string) {
  return useQuery({
    queryKey: [...QUERY_KEYS.analytics.summary(brandId, range), month ?? "this"],
    queryFn: async () =>
      (
        await call(
          api.GET(ENDPOINTS.analyticsSummary, {
            params: { path: { brandId }, query: { range, ...(month ? { month } : {}) } },
          }),
        )
      ).data,
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** One day: meals by slot, dishes, estimated revenue. */
export function useAnalyticsDay(brandId: string, date: string) {
  return useQuery({
    queryKey: QUERY_KEYS.analytics.day(brandId, date),
    queryFn: async () =>
      (await call(api.GET(ENDPOINTS.analyticsDay, { params: { path: { brandId }, query: { date } } }))).data,
    placeholderData: keepPreviousData,
  });
}

/** One month: each day's meals and estimated revenue, plans bought and cancelled. */
export function useAnalyticsMonth(brandId: string, month: string) {
  return useQuery({
    queryKey: QUERY_KEYS.analytics.month(brandId, month),
    queryFn: async () =>
      (await call(api.GET(ENDPOINTS.analyticsMonth, { params: { path: { brandId }, query: { month } } })))
        .data,
    placeholderData: keepPreviousData,
  });
}

/** Every plan: subscribers, sales, revenue, churn. */
export function useAnalyticsPlans(brandId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.analytics.plans(brandId),
    queryFn: async () =>
      (await call(api.GET(ENDPOINTS.analyticsPlans, { params: { path: { brandId } } }))).data,
  });
}
